import React, { useState, useRef, useEffect } from 'react';
import Icon from '../AppIcon';
import useTranslation from '../../hooks/useTranslation';

/**
 * GroupedSelect — custom dropdown with grouped options and non-selectable section headers.
 * Matches SearchableSelect's visual style: panel below field, orange highlight on active/selected.
 *
 * Props:
 *   groups       – array of { label: string, options: [{ value: string, label: string }] }
 *   value        – currently selected value (string | '')
 *   onChange     – (value: string) => void
 *   placeholder  – string (default : t('common.selectPlaceholder'))
 *   className    – extra class names for the wrapper
 *   disabled     – boolean
 */
const GroupedSelect = ({
  groups = [],
  value,
  onChange,
  placeholder = null,
  className = '',
  disabled = false,
}) => {
  const { t } = useTranslation();
  // Le defaut ne peut pas vivre dans la signature : t() vient du hook.
  const resolvedPlaceholder = placeholder ?? t('common.selectPlaceholder');
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  // Flatten all options for label lookup
  const allOptions = groups?.flatMap(g => g?.options || []);
  const selectedOption = allOptions?.find(o => String(o?.value) === String(value ?? '')) || null;
  const displayLabel = selectedOption?.label || '';

  // Close on outside click
  useEffect(() => {
    const handleOutside = (e) => {
      if (containerRef?.current && !containerRef?.current?.contains(e?.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, []);

  const handleToggle = () => {
    if (!disabled) setOpen(prev => !prev);
  };

  const handleSelect = (optValue) => {
    onChange(optValue);
    setOpen(false);
  };

  const handleClear = (e) => {
    e?.stopPropagation();
    onChange('');
  };

  const hasValue = value !== null && value !== undefined && value !== '';

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Trigger */}
      <div className="relative">
        <button
          type="button"
          onClick={handleToggle}
          disabled={disabled}
          className="w-full pl-3 pr-8 py-2 text-xs border border-gray-200 rounded-lg outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 bg-white text-left truncate disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {hasValue ? (
            <span className="text-gray-900 truncate block">{displayLabel}</span>
          ) : (
            <span className="text-gray-400 truncate block">{resolvedPlaceholder}</span>
          )}
        </button>
        {/* Chevron / Clear */}
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-0.5 pointer-events-none">
          {hasValue ? (
            <button
              type="button"
              onMouseDown={handleClear}
              className="p-0.5 rounded text-gray-300 hover:text-gray-500 transition-colors pointer-events-auto"
              tabIndex={-1}
            >
              <Icon name="X" size={12} />
            </button>
          ) : (
            <Icon name={open ? 'ChevronUp' : 'ChevronDown'} size={12} className="text-gray-400" />
          )}
        </div>
      </div>

      {/* Dropdown panel */}
      {open && !disabled && (
        <div
          className="absolute z-[9999] mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden"
          style={{ minWidth: '160px', maxHeight: '260px', overflowY: 'auto' }}
        >
          {/* Empty option */}
          <button
            type="button"
            onMouseDown={() => handleSelect('')}
            className={`w-full text-left px-3 py-2 text-xs transition-colors ${
              !hasValue ? 'bg-orange-50 text-primary' : 'text-gray-400 italic hover:bg-gray-50'
            }`}
          >
            {resolvedPlaceholder}
          </button>

          {groups?.map((group, gi) => (
            <div key={gi}>
              {/* Section header — non-selectable */}
              <div className="px-3 py-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wide bg-gray-50 border-t border-gray-100 select-none">
                {group?.label}
              </div>
              {/* Options */}
              {(group?.options || [])?.map((opt) => {
                const isSelected = String(value ?? '') === String(opt?.value);
                return (
                  <button
                    key={opt?.value}
                    type="button"
                    onMouseDown={() => handleSelect(opt?.value)}
                    className={`w-full text-left px-3 py-2 text-xs transition-colors flex items-center justify-between gap-2 ${
                      isSelected
                        ? 'bg-orange-50 text-primary'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <span className="truncate">{opt?.label}</span>
                    {isSelected && <Icon name="Check" size={10} className="shrink-0 text-primary" />}
                  </button>
                );
              })}
            </div>
          ))}

          {groups?.length === 0 && (
            <div className="px-3 py-2.5 text-xs text-gray-400 italic">{t('common.noOptions')}</div>
          )}
        </div>
      )}
    </div>
  );
};

export default GroupedSelect;
